import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useData } from '../../contexts/DataContext';
import { useLocation, useNavigate } from 'react-router-dom';

interface CompanySelectorProps {
  className?: string;
  showColorIndicator?: boolean;
  colorOverride?: string;
}

const CompanySelector = ({
  className = '',
  showColorIndicator = false,
  colorOverride = undefined,
}: CompanySelectorProps) => {
  const { t } = useTranslation();
  const { companies, setTargetCompany, targetCompany } = useData();
  const location = useLocation();
  const navigate = useNavigate();
  const [isCompanyPopupOpen, setIsCompanyPopupOpen] = useState(false);
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const popupRef = useRef<HTMLDivElement>(null);
  const arrowButtonRef = useRef<HTMLButtonElement>(null);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        setIsCompanyPopupOpen(false);
        setCompanySearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCompanyChange = (companyId: string) => {
    setTargetCompany(companyId);
    setIsCompanyPopupOpen(false);
    setCompanySearchTerm('');

    // Only update URL if we're on a company path
    if (location.pathname.startsWith('/company/')) {
      const pathParts = location.pathname.split('/');
      // Replace the company ID in the URL
      pathParts[2] = companyId;
      navigate(pathParts.slice(0, 3).join('/'));
    }
  };

  // Filter companies based on search term
  const filteredCompanies = companies.filter((company) =>
    company.name.toLowerCase().includes(companySearchTerm.toLowerCase())
  );

  const selectedCompany = companies.find((c) => c.id === targetCompany);

  return (
    <div className={`flex items-center justify-end ${className}`} id="company-select-container">
      {/* Company Display */}
      <div className="flex items-center">
        {selectedCompany ? (
          <>
            {showColorIndicator && (
              <div className={`w-3 h-3 rounded-full mr-2 ${colorOverride || 'bg-green-500'}`}></div>
            )}
            <span className="text-sm font-medium text-gray-800 truncate">
              {selectedCompany.name}
            </span>
          </>
        ) : (
          <>
            {showColorIndicator && <div className="w-3 h-3 rounded-full mr-2 bg-gray-400"></div>}
            <span className="text-xs text-gray-500">
              {companies.length > 0 ? t('common.select_company') : t('common.no_companies')}
            </span>
          </>
        )}
      </div>

      {/* Arrow Button */}
      {companies.length > 0 && (
        <div className="relative">
          <button
            ref={arrowButtonRef}
            onClick={() => setIsCompanyPopupOpen(!isCompanyPopupOpen)}
            className="ml-2 p-1 hover:bg-gray-200 rounded transition-colors duration-200"
            aria-label={t('common.select_company_button')}
          >
            <span className="material-symbols-outlined text-sm text-gray-600">
              {isCompanyPopupOpen ? 'expand_less' : 'expand_more'}
            </span>
          </button>

          {/* Company Selection Popup */}
          <div
            ref={popupRef}
            style={{ left: '0', top: '25px' }}
            className={`absolute top-full right-0 mt-1 bg-white rounded-lg shadow-xl w-80 max-h-80 flex-col border border-gray-200 z-50 ${
              isCompanyPopupOpen ? 'flex' : 'hidden'
            }`}
          >
            {/* Header */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <p className=" text-sm text-gray-400">{t('common.companies')}</p>
                <button
                  onClick={() => {
                    setIsCompanyPopupOpen(false);
                    setCompanySearchTerm('');
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                >
                  <span className="material-symbols-outlined text-sm">close</span>
                </button>
              </div>

              {/* Search Field */}
              {/* <div className="mt-3 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="material-symbols-outlined text-gray-400 text-sm">search</span>
                </div>
                <input
                  type="text"
                  placeholder={t('common.find_company_placeholder')}
                  value={companySearchTerm}
                  onChange={(e) => setCompanySearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div> */}
            </div>

            {/* Company List */}
            <div className="flex-1 overflow-y-auto">
              {filteredCompanies.length > 0 ? (
                <div className="py-2">
                  {filteredCompanies.map((company) => (
                    <button
                      key={company.id}
                      onClick={() => handleCompanyChange(company.id)}
                      className="w-full px-4 py-3 text-left hover:bg-gray-100 transition-colors duration-200 flex items-center justify-between group"
                    >
                      <span className="text-sm font-medium text-gray-900">{company.name}</span>
                      {targetCompany === company.id && (
                        <span className="material-symbols-outlined text-green-500 text-lg">
                          check
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-4 text-center text-gray-500 text-sm">
                  {companySearchTerm ? t('common.no_companies_found') : t('common.no_companies')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanySelector;
